/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
/**
 * Configuration API service for admin interface
 * Handles communication with the config edge function
 */

import type {
  DbConfigValidationRules as ConfigValidationRules,
  DbConfigItem,
  DbProcessConfig as ProcessConfig,
  DbProcessQueueConfig as ProcessQueueConfig,
  DbTimeoutConfig as TimeoutConfig,
  DbCleanupConfig as CleanupConfig,
  DbCronConfig as CronConfig,
  DbAppConfig as AppConfig,
} from '@shared/types/config';
import { supabase } from './supabase';

export type { AppConfig };
export type ConfigItem = DbConfigItem<
  string | number | boolean | Record<string, unknown>
>;

export class ConfigApiService {
  private static async invokeFunction<T>(
    payload: Record<string, unknown>
  ): Promise<T> {
    const { data, error } = await supabase.functions.invoke('config-api', {
      body: payload,
    });

    if (error) {
      throw new Error(error.message || 'Configuration function call failed');
    }

    return (data ?? null) as T;
  }

  /**
   * Get all configurations
   */
  static async getAllConfigurations(): Promise<ConfigItem[]> {
    return ConfigApiService.invokeFunction<ConfigItem[]>({ action: 'getAll' });
  }

  /**
   * Get configurations by category
   */
  static async getConfigurationsByCategory(
    category: string
  ): Promise<ConfigItem[]> {
    return ConfigApiService.invokeFunction<ConfigItem[]>({
      action: 'getByCategory',
      category,
    });
  }

  /**
   * Get process configurations as a typed object
   */
  static async getProcessConfig(): Promise<{
    items: ConfigItem[];
    config: ProcessConfig;
  }> {
    const items = await ConfigApiService.getConfigurationsByCategory('process');

    // Transform to typed config object
    const config: Record<string, unknown> = {};
    for (const item of items) {
      config[item.key] = item.value;
    }

    return { items, config: config as ProcessConfig };
  }

  /**
   * Get all configurations as a typed object
   */
  static async getAppConfig(): Promise<{
    items: ConfigItem[];
    config: AppConfig;
  }> {
    const items = await ConfigApiService.getAllConfigurations();

    // Transform to typed config object
    const config: Record<string, unknown> = {};
    for (const item of items) {
      config[item.key] = item.value;
    }

    return { items, config: config as AppConfig };
  }

  /**
   * Get cron configurations as a typed object
   */
  static async getCronConfig(): Promise<{
    items: ConfigItem[];
    config: CronConfig;
  }> {
    const items = await ConfigApiService.getConfigurationsByCategory('cron');

    // Transform to typed config object
    const config: Record<string, unknown> = {};
    for (const item of items) {
      config[item.key] = item.value;
    }

    return { items, config: config as CronConfig };
  }

  /**
   * Update a configuration value
   */
  static async updateConfiguration(
    key: string,
    value: unknown
  ): Promise<ConfigItem> {
    return ConfigApiService.invokeFunction<ConfigItem>({
      action: 'update',
      key,
      value,
    });
  }

  /**
   * Update multiple configurations at once
   */
  static async updateMultipleConfigurations(
    updates: { key: string; value: unknown }[]
  ): Promise<ConfigItem[]> {
    // Execute updates in parallel
    const promises = updates.map(update =>
      ConfigApiService.updateConfiguration(update.key, update.value)
    );

    return Promise.all(promises);
  }

  /**
   * Validate a configuration value before sending
   */
  static validateConfigValue(
    value: unknown,
    type: ConfigItem['type'],
    validationRules?: ConfigValidationRules
  ): { valid: boolean; error?: string } {
    // Type validation
    switch (type) {
      case 'number':
        if (typeof value !== 'number' || Number.isNaN(value)) {
          return { valid: false, error: 'Value must be a valid number' };
        }
        break;
      case 'string':
      case 'text':
        if (typeof value !== 'string') {
          return { valid: false, error: 'Value must be a string' };
        }
        break;
      case 'boolean':
        if (typeof value !== 'boolean') {
          return { valid: false, error: 'Value must be a boolean' };
        }
        break;
      case 'json':
        if (typeof value !== 'object' || value === null) {
          return { valid: false, error: 'Value must be a valid JSON object' };
        }
        break;
      default:
        return { valid: false, error: `Unknown type: ${type}` };
    }

    // Validation rules
    if (validationRules) {
      // Number range validation
      if (type === 'number' && typeof value === 'number') {
        if (validationRules.min !== undefined && value < validationRules.min) {
          return {
            valid: false,
            error: `Value must be at least ${validationRules.min}`,
          };
        }
        if (validationRules.max !== undefined && value > validationRules.max) {
          return {
            valid: false,
            error: `Value must be at most ${validationRules.max}`,
          };
        }
      }

      // Required fields validation for JSON objects
      if (
        type === 'json' &&
        validationRules.required_fields &&
        typeof value === 'object'
      ) {
        for (const field of validationRules.required_fields) {
          if (!(field in value)) {
            return { valid: false, error: `Required field missing: ${field}` };
          }
        }
      }

      // Required variables validation for text/prompts
      if (
        (type === 'text' || type === 'string') &&
        validationRules.required_variables &&
        typeof value === 'string'
      ) {
        for (const variable of validationRules.required_variables) {
          if (!value.includes(variable)) {
            return {
              valid: false,
              error: `Required variable missing: ${variable}`,
            };
          }
        }
      }
    }

    return { valid: true };
  }
}

export const configApi = {
  getAllConfigurations: ConfigApiService.getAllConfigurations,
  getConfigurationsByCategory: ConfigApiService.getConfigurationsByCategory,
  getProcessConfig: ConfigApiService.getProcessConfig,
  getAppConfig: ConfigApiService.getAppConfig,
  getCronConfig: ConfigApiService.getCronConfig,
  updateConfiguration: ConfigApiService.updateConfiguration,
  updateMultipleConfigurations: ConfigApiService.updateMultipleConfigurations,
};
